import { Injectable } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { BillingService, PurchaseItem } from '@galaxy/billing';
import { combineLatest, Observable, of } from 'rxjs';
import { PurchaseItemRow } from './interface';
import { first, map, shareReplay } from 'rxjs/operators';

@Injectable()
export class PurchaseItemRowService {
  private customers: AccountGroup[] = [];
  private projectionFilter = new ProjectionFilter({ napData: true });

  constructor(private accountGroupService: AccountGroupService, private billingService: BillingService) {}

  buildItemRows(purchaseItems: PurchaseItem[]): Observable<PurchaseItemRow[]> {
    // get business if it has not been previously fetched
    const cachedCustomerIds = this.customers.map((ag) => ag.accountGroupId) || [];
    const allCustomerIds =
      purchaseItems.filter((item) => item.customerId !== item.merchantId).map((pi) => pi.customerId) || [];
    const customersToFetch = Array.from(new Set(allCustomerIds.filter((id) => !cachedCustomerIds.includes(id)))) || [];

    let customers$: Observable<AccountGroup[]>;
    if (customersToFetch.length > 0) {
      customers$ = this.accountGroupService.getMulti(customersToFetch, this.projectionFilter).pipe(
        map((ags) => {
          this.customers = this.customers.concat(ags.filter((ag) => !!ag));
          return this.customers;
        }),
      );
    } else {
      customers$ = of(this.customers);
    }

    // get products for purchase item skus but remove duplicates
    const productIds = Array.from(new Set(purchaseItems.map((pi) => pi.sku)));

    const products$ = this.billingService.getMultiProduct(productIds);
    return combineLatest([products$, customers$]).pipe(
      first(),
      map(([products, customers]) => {
        return purchaseItems.map((pi) => {
          const product = products.get(pi.sku);
          const liProduct = product ? product.name : 'N/A';
          const liDescription = pi.description ? `: ${pi.description}` : '';
          const name = `${liProduct}${liDescription}`;
          let companyName: string;
          if (pi.merchantId === pi.customerId) {
            companyName = pi.merchantId;
          } else {
            const customer = customers.find((ag) => ag.accountGroupId === pi.customerId);
            companyName = customer ? customer.napData.companyName : pi.customerId;
          }
          return { ...pi, name, companyName } as PurchaseItemRow;
        });
      }),
      shareReplay(1),
    );
  }
}
